import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, User } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import SocialLinks from "@/components/SocialLinks";
import { KD9_CONTACT } from "@/lib/contact";
import { useLanguage } from "@/context/LanguageContext";

const Contacto = () => {
  const { t, lang } = useLanguage();
  const [form,setForm]=useState({name:"",email:"",phone:"",message:""});

  const submit=(event)=>{
    event.preventDefault();
    const text=[
      lang === "es" ? "Hola Khaos Delta 9, deseo comunicarme." : "Hello Khaos Delta 9, I would like to get in touch.",
      `${lang === "es" ? "Nombre" : "Name"}: ${form.name}`,
      `${lang === "es" ? "Correo" : "Email"}: ${form.email || (lang === "es" ? "No indicado" : "Not provided")}`,
      `${lang === "es" ? "Teléfono" : "Phone"}: ${form.phone || (lang === "es" ? "No indicado" : "Not provided")}`,
      `${lang === "es" ? "Mensaje" : "Message"}: ${form.message}`,
    ].join("\n");
    window.open(`${KD9_CONTACT.whatsappUrl}?text=${encodeURIComponent(text)}`,"_blank","noopener,noreferrer");
  };

  const field=(key)=>(event)=>setForm({...form,[key]:event.target.value});

  return (
    <div className="min-h-screen" data-testid="contacto-page">
      <PublicNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div className="font-mont text-[11px] tracking-[0.45em] uppercase mb-5" style={{color:"var(--kd9-accent)"}}>· {t("nav.contacto")} ·</div>
        <h1 className="font-cinzel kd9-gold-text mb-4 leading-[1.05]" style={{fontSize:"clamp(2.4rem, 5vw, 4rem)",fontWeight:700,letterSpacing:"0.04em"}}>{t("contact.title").toUpperCase()}</h1>
        <p className="font-cormorant text-xl max-w-2xl" style={{color:"var(--kd9-text-soft)"}}>{t("contact.subtitle")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 grid lg:grid-cols-[1fr_400px] gap-8">
        <form className="kd9-card p-8 lg:p-10 space-y-5" onSubmit={submit} data-testid="contacto-form">
          <Field icon={User} label={t("contact.name")}><input required value={form.name} onChange={field("name")} className="kd9-input" placeholder={t("contact.name")} data-testid="contacto-name"/></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field icon={Mail} label={t("contact.email")}><input type="email" value={form.email} onChange={field("email")} className="kd9-input" placeholder="tu@correo.com" data-testid="contacto-email"/></Field>
            <Field icon={Phone} label={t("contact.phone")}><input value={form.phone} onChange={field("phone")} className="kd9-input" placeholder="+57 000 000 0000" data-testid="contacto-phone"/></Field>
          </div>
          <div>
            <label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{color:"var(--kd9-text-soft)"}}>{t("contact.message")}</label>
            <textarea required rows={6} value={form.message} onChange={field("message")} className="kd9-input pl-4 resize-none" placeholder={t("contact.message")} data-testid="contacto-message"/>
          </div>
          <button type="submit" className="kd9-btn-primary w-full py-4 rounded-xl text-[12px] flex items-center justify-center gap-3" data-testid="contacto-submit"><Send size={14}/> {t("contact.whatsappSend")}</button>
        </form>

        <aside className="space-y-5">
          <ContactCard icon={Phone} title={t("contact.phoneWhatsapp")} body={KD9_CONTACT.phoneDisplay} tone="#1FA774" href={KD9_CONTACT.whatsappUrl}/>
          <ContactCard icon={Mail} title={t("contact.email")} body={KD9_CONTACT.email} tone="#3C78B5" href={`mailto:${KD9_CONTACT.email}`}/>
          <ContactCard icon={MapPin} title={t("contact.location")} body={KD9_CONTACT.location} tone="#D4AF37" href={KD9_CONTACT.mapUrl}/>
          <div className="kd9-card p-5">
            <div className="font-mont text-[11px] tracking-[0.3em] uppercase mb-4" style={{color:"var(--kd9-text-soft)"}}>{t("footer.follow")}</div>
            <SocialLinks testIdPrefix="contacto-social"/>
          </div>
          <a href={KD9_CONTACT.whatsappUrl} target="_blank" rel="noopener noreferrer" className="kd9-card p-5 flex items-center gap-3 text-sm font-semibold" style={{color:"var(--kd9-success)"}}><MessageCircle size={19}/> {t("contact.whatsappStart")}</a>
          <div className="kd9-card p-6 text-center" style={{background:"linear-gradient(135deg, var(--kd9-accent-soft) 0%, transparent 100%)"}}>
            <p className="font-cormorant italic text-lg" style={{color:"var(--kd9-text)"}}>&ldquo;{t("footer.lema")}&rdquo;</p>
          </div>
        </aside>
      </section>
      <Footer />
    </div>
  );
};

const Field=({icon:Icon,label,children})=><div><label className="font-mont text-[11px] tracking-[0.25em] uppercase mb-2 block" style={{color:"var(--kd9-text-soft)"}}>{label}</label><div className="relative"><Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{color:"var(--kd9-accent)"}}/>{children}</div></div>;

const ContactCard=({icon:Icon,title,body,tone,href})=><a href={href} target={href.startsWith("http")?"_blank":undefined} rel={href.startsWith("http")?"noopener noreferrer":undefined} className="kd9-card p-5 flex items-center gap-4 hover:-translate-y-0.5 transition"><div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{background:`${tone}22`,border:`1px solid ${tone}55`}}><Icon size={18} style={{color:tone}}/></div><div><div className="font-mont text-[11px] tracking-[0.3em] uppercase mb-1" style={{color:"var(--kd9-text-soft)"}}>{title}</div><div className="font-mont text-sm font-semibold">{body}</div></div></a>;

export default Contacto;
